import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { Liquiditaet } from '../../services/erpSteering';
import { formatCents } from '../../services/freelancerLogic';
import { KartenRahmen } from './KartenRahmen';

// Liquiditaetsvorschau ueber 13 Wochen (ERP/CRM-Plan Abschnitt 8).
//
// Inline-SVG, keine Chart-Bibliothek — bewusst wie im uebrigen Projekt
// (QuoteCharts: „das Projekt zieht bewusst keine Chart-Library"). Balken je
// Woche fuer Ein- und Auszahlungen, darueber die Linie des kumulierten
// Verlaufs; die Nulllinie ist eingezeichnet, weil sie die Warnschwelle IST.

interface Props {
    zustand: { daten: Liquiditaet | null; laedt: boolean; fehler: string | null };
}

const BREITE = 780;
const HOEHE = 240;
const RAND_LINKS = 8;
const RAND_UNTEN = 26;
const RAND_OBEN = 12;

const kw = (von: string): string => {
    const [j, m, t] = von.split('-');
    return `${t}.${m}.${j.slice(2)}`;
};

export const LiquiditaetCard: React.FC<Props> = ({ zustand }) => {
    const d = zustand.daten;

    return (
        <KartenRahmen laedt={zustand.laedt} fehler={zustand.fehler}>
            {d && (() => {
                const wochen = d.wochen;
                const werte = wochen.flatMap((w) => [w.eingangCents, w.auszahlungCents, w.kumuliertCents]);
                const max = Math.max(0, ...werte);
                const min = Math.min(0, ...werte);
                const spanne = max - min || 1;

                const nutzHoehe = HOEHE - RAND_UNTEN - RAND_OBEN;
                const y = (wert: number): number => RAND_OBEN + ((max - wert) / spanne) * nutzHoehe;
                const spaltenBreite = (BREITE - RAND_LINKS * 2) / wochen.length;
                const balkenBreite = Math.max(4, spaltenBreite * 0.32);
                const x = (i: number): number => RAND_LINKS + i * spaltenBreite + spaltenBreite / 2;

                const nulllinie = y(0);
                const linie = wochen.map((w, i) => `${i === 0 ? 'M' : 'L'}${x(i)},${y(w.kumuliertCents)}`).join(' ');

                return (
                    <div className="space-y-5">
                        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                            <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
                                <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500">Erwartete Eingänge</p>
                                <p className="mt-1 text-lg font-bold tabular-nums text-emerald-700">
                                    {formatCents(wochen.reduce((s, w) => s + w.eingangCents, 0))}
                                </p>
                            </div>
                            <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
                                <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500">Auszahlungen</p>
                                <p className="mt-1 text-lg font-bold tabular-nums text-red-600">
                                    {formatCents(wochen.reduce((s, w) => s + w.auszahlungCents, 0))}
                                </p>
                            </div>
                            <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
                                <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500">Tiefster Stand</p>
                                <p
                                    className={`mt-1 text-lg font-bold tabular-nums ${
                                        d.minKumuliertCents < 0 ? 'text-red-600' : 'text-slate-800'
                                    }`}
                                >
                                    {formatCents(d.minKumuliertCents)}
                                </p>
                            </div>
                            <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
                                <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500">Überfällig</p>
                                <p className="mt-1 text-lg font-bold tabular-nums text-amber-700">
                                    {formatCents(d.ueberfaelligCents)}
                                </p>
                                <p className="mt-0.5 text-xs text-slate-500">in Woche 1 verbucht</p>
                            </div>
                        </div>

                        {d.ersteUnterdeckung !== null && (
                            <div className="flex gap-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                                <p>
                                    Der kumulierte Verlauf fällt in <strong>Woche {d.ersteUnterdeckung}</strong>{' '}
                                    unter null ({formatCents(d.minKumuliertCents)} im Tief). Ohne Bankguthaben oder
                                    zusätzliche Eingänge entsteht dort eine Deckungslücke.
                                </p>
                            </div>
                        )}

                        <div className="overflow-x-auto">
                            <svg
                                viewBox={`0 0 ${BREITE} ${HOEHE}`}
                                className="h-60 w-full min-w-[640px]"
                                role="img"
                                aria-label="Liquiditätsvorschau über 13 Wochen"
                            >
                                <title>Erwartete Ein- und Auszahlungen je Woche, dazu der kumulierte Verlauf</title>
                                {/* Nulllinie = Warnschwelle */}
                                <line
                                    x1={RAND_LINKS}
                                    x2={BREITE - RAND_LINKS}
                                    y1={nulllinie}
                                    y2={nulllinie}
                                    stroke="#94a3b8"
                                    strokeWidth={1}
                                    strokeDasharray="4 3"
                                />
                                {wochen.map((w, i) => {
                                    const mitte = x(i);
                                    const yEin = y(Math.max(0, w.eingangCents));
                                    const yAus = y(Math.min(0, w.auszahlungCents));
                                    return (
                                        <g key={w.index}>
                                            <rect
                                                x={mitte - balkenBreite - 1}
                                                y={yEin}
                                                width={balkenBreite}
                                                height={Math.max(0, nulllinie - yEin)}
                                                fill="#10b981"
                                                opacity={0.85}
                                            >
                                                <title>{`Woche ${w.index} (${kw(w.von)}): Eingang ${formatCents(w.eingangCents)}`}</title>
                                            </rect>
                                            <rect
                                                x={mitte + 1}
                                                y={nulllinie}
                                                width={balkenBreite}
                                                height={Math.max(0, yAus - nulllinie)}
                                                fill="#ef4444"
                                                opacity={0.85}
                                            >
                                                <title>{`Woche ${w.index} (${kw(w.von)}): Auszahlung ${formatCents(w.auszahlungCents)}`}</title>
                                            </rect>
                                            <text
                                                x={mitte}
                                                y={HOEHE - 8}
                                                textAnchor="middle"
                                                className="fill-slate-400"
                                                fontSize={9}
                                            >
                                                {kw(w.von)}
                                            </text>
                                        </g>
                                    );
                                })}
                                <path d={linie} fill="none" stroke="#2563eb" strokeWidth={2} />
                                {wochen.map((w, i) => (
                                    <circle key={w.index} cx={x(i)} cy={y(w.kumuliertCents)} r={3} fill="#2563eb">
                                        <title>{`Woche ${w.index}: kumuliert ${formatCents(w.kumuliertCents)}`}</title>
                                    </circle>
                                ))}
                            </svg>
                        </div>

                        {/* Listenalternative zur Grafik (Plan 11 „Accessibility"). */}
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-slate-200 text-left text-[11px] uppercase tracking-widest text-slate-500">
                                        <th className="py-2 pr-3 font-bold">Woche</th>
                                        <th className="py-2 pr-3 text-right font-bold">Eingang</th>
                                        <th className="py-2 pr-3 text-right font-bold">Auszahlung</th>
                                        <th className="py-2 pr-3 text-right font-bold">Netto</th>
                                        <th className="py-2 text-right font-bold">Kumuliert</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {wochen.map((w) => (
                                        <tr key={w.index} className="border-b border-slate-100">
                                            <td className="py-1.5 pr-3 text-slate-600">
                                                {w.index}. Woche <span className="text-xs text-slate-400">({kw(w.von)}–{kw(w.bis)})</span>
                                            </td>
                                            <td className="py-1.5 pr-3 text-right tabular-nums">{formatCents(w.eingangCents)}</td>
                                            <td className="py-1.5 pr-3 text-right tabular-nums">{formatCents(w.auszahlungCents)}</td>
                                            <td className="py-1.5 pr-3 text-right tabular-nums">{formatCents(w.nettoCents)}</td>
                                            <td
                                                className={`py-1.5 text-right font-semibold tabular-nums ${
                                                    w.kumuliertCents < 0 ? 'text-red-600' : 'text-slate-700'
                                                }`}
                                            >
                                                {formatCents(w.kumuliertCents)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {(d.spaeterCents !== 0 || d.ohneFaelligkeitCents !== 0) && (
                            <p className="text-xs text-slate-500">
                                Nicht im Raster enthalten: {formatCents(d.spaeterCents)} fällig nach der 13. Woche,
                                {' '}{formatCents(d.ohneFaelligkeitCents)} ohne Fälligkeitsdatum.
                            </p>
                        )}
                    </div>
                );
            })()}
        </KartenRahmen>
    );
};
